import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { KTSVG } from '../../../_metronic/helpers'
import { useSearchableMembersContext } from '../../context/SearchableMembersContext'
import { useUserProfileContext } from '../../context/UserProfileContext'
import { useCategoriesMembers } from '../../hooks/members/useCategoriesMembers'
import { useSearchableMembers } from '../../hooks/members/useSearchableMembers'
import { getSearchableExperiences } from '../../services/membersService'
import { parseResultsSearch } from '../search/resultsParams'
import { SearchType } from '../search/types'
import { MemberExperiences } from './components/MemberExperiences'
import { MembersCategoriesList } from './components/MembersCategoriesList'
import { MemberSearchList } from './components/MemberSearchList'
import { NearbyMembers } from './components/NearbyMembers'
import { PromoBanners } from './components/PromoBanners'
import { WelcomeBanner } from './components/WelcomeBanner'

// Dashboard caps: /main-dashboard shows the N most recent members / experiences.
// Bounded slice is enforced server-side via ?limit= AND defensively client-side
// via .slice() so a backend regression can't overflow the layout.
//
// Members: single horizontal row with scroll → cap at 10 most recent active members
// (backend already restricts to active membership; we only tighten the limit here).
const DASHBOARD_MEMBERS_LIMIT = 10
// Invitations: 8 experiencias recientes filtrables por categoria.
const DASHBOARD_EXPERIENCES_LIMIT = 8
// Most Popular: mientras no exista el algoritmo de popularidad, pedimos 16 al
// mismo endpoint (recientes) y en el front descartamos las 8 que ya salen en
// Invitations para no ver las mismas fotos dos veces. Cuando el backend gane
// un endpoint dedicado ponderado, este numero baja a 8.
const POPULAR_FETCH_LIMIT = 16
const POPULAR_DISPLAY_LIMIT = 8

const MembersPage = () => {
  const {data: categories} = useCategoriesMembers()
  // The global SearchableMembersProvider also feeds /members-search (which must remain
  // unpaged/unbounded), so we deliberately DO NOT read `data` from the context here.
  // Instead, the dashboard drives its own bounded fetch through useSearchableMembers
  // with limit=15. The context is still consumed for search params so header-search-bar
  // navigation and filter reset flows keep working.
  const {setSearchParams: setParamasData} = useUserProfileContext()
  const {setSearchParams, searchParams, loading} = useSearchableMembersContext()
  const {data: membersRaw, loading: membersLoading} = useSearchableMembers(
    searchParams.where,
    searchParams.keywords,
    searchParams.category ?? '',
    DASHBOARD_MEMBERS_LIMIT,
  )
  // Defensive slice — if the backend ignores ?limit= we still render at most 15 cards.
  const members = Array.isArray(membersRaw) ? membersRaw.slice(0, DASHBOARD_MEMBERS_LIMIT) : []
  const [ experiences, setExperiences ] = useState<any[]>([])
  // "Most Popular" es un segundo carrusel sin filtro por categoria. De momento
  // reusa el mismo endpoint (recientes) y descartamos overlaps con Invitations.
  const [ popularExperiences, setPopularExperiences ] = useState<any[]>([])
  const [ selectedCategoryId, setSelectedCategoryId ] = useState<string | null>(null)
  const location = useLocation()

  useEffect(() => {
    let container = document.getElementById('kt_content_container')
    container?.classList.add('member-full-wrapper')
    document.body.classList.add('main-dashboard-active')
    return () => {
      container?.classList.remove('member-full-wrapper')
      document.body.classList.remove('main-dashboard-active')
    }
  }, [])

  useEffect(() => {
    const {q, type} = parseResultsSearch(location.search)
    if (q) {
      if (q !== (searchParams.query ?? '')) {
        setSearchParams({where: '', keywords: q, category: '', query: q, type})
      }
      return
    }
    // Landing sin query (login o click en logo): limpiar filtros para mostrar
    // el máximo permitido en Members y Experiencias.
    const hasFilters =
      !!searchParams.where ||
      !!searchParams.keywords ||
      !!searchParams.category ||
      !!searchParams.query ||
      (searchParams.type ?? 'all') !== 'all'
    if (hasFilters) {
      setSearchParams({where: '', keywords: '', category: '', query: '', type: 'all'})
    }
    // Reset también la categoría de experiencias y re-fetch para garantizar el
    // máximo permitido cuando el único filtro previo era la categoría.
    if (selectedCategoryId !== null) {
      setSelectedCategoryId(null)
    }
    getExperiences()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  useEffect(() => {
    if(loading) return;
    getExperiences();
  }, [setSearchParams, setParamasData, searchParams.keywords, searchParams.where])

  const getExperiences = async (category?:number) => {
    // /main-dashboard always shows the DASHBOARD_EXPERIENCES_LIMIT most recent experiences —
    // the header search bar navigates to /experiences-search rather than filtering this view,
    // so we always request the bounded slice here. Defensive .slice() mirrors the members path.
    const experiences_ = await getSearchableExperiences(
      searchParams.where,
      searchParams.keywords,
      category,
      DASHBOARD_EXPERIENCES_LIMIT,
    );
    const bounded = Array.isArray(experiences_)
      ? experiences_.slice(0, DASHBOARD_EXPERIENCES_LIMIT)
      : []
    setExperiences(bounded);
    // Fetch paralelo de "Most Popular" (misma sesion, siempre sin categoria
    // porque esa fila no acepta filtros). Pedimos POPULAR_FETCH_LIMIT y
    // filtramos los ids que ya salen arriba para evitar duplicados visuales.
    // Nota: se dispara con cada cambio de categoria del carrusel principal
    // porque compartimos el mismo `searchParams.where/keywords`; si esos no
    // cambiaron la respuesta cachea rapido en backend.
    const popularRaw = await getSearchableExperiences(
      searchParams.where,
      searchParams.keywords,
      undefined,
      POPULAR_FETCH_LIMIT,
    );
    const invitationsIds = new Set(bounded.map((e: any) => e?.id).filter(Boolean))
    const filtered = Array.isArray(popularRaw)
      ? popularRaw.filter((e: any) => e?.id && !invitationsIds.has(e.id))
      : []
    setPopularExperiences(filtered.slice(0, POPULAR_DISPLAY_LIMIT))
  }

  const activeType: SearchType = searchParams.type ?? 'all'
  const showAll = activeType === 'all'
  const show = (t: SearchType) => showAll || activeType === t

  return (
    <>
      <div className='members-page'>
        <WelcomeBanner />

        {show('members') && (
          <>
            <div className='members-page__header'>
              {/* PR-I-54: title deep-links to the full members search (no filters).
                  Uses the same KTSVG waitlist glyph as the Invitations title so both
                  section headers render an identical symbol on the dashboard. */}
              <Link to='/members-search' className='members-page__title-link'>
                <h1 className='members-page__title'>
                  Members&nbsp;<KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
                </h1>
              </Link>
            </div>

            <MembersCategoriesList data={categories} />
            <MemberSearchList data={members} loading={membersLoading} />
            <NearbyMembers search={searchParams.where}/>
          </>
        )}

        {show('experiences') && (
          <>
            <MemberExperiences items={experiences} popularItems={popularExperiences} onChangeCategory={getExperiences} search={searchParams.where} selectedCategoryId={selectedCategoryId} onSelectedCategoryChange={setSelectedCategoryId} />
            <PromoBanners variant='bordered' />
          </>
        )}
      </div>
    </>
  )
}

export { MembersPage }
